import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import {
  liveFieldsFromInput,
  toOptimisticFeature,
  type FeatureInput,
} from '@/features/patient-record/lib/featureForm';
import { mapFeature } from '@/features/patient-record/mappers/feature';
import { patientQueries } from '@/features/patient-record/queries';
import type { FeatureViewModel } from '@/features/patient-record/types';

export type { FeatureInput };

/**
 * Resource-level mutations for HPO phenotypic features. All three calls
 * speak the live `FeatureDTO` shape (`termId`, three-state `observed`,
 * structured qualifier groups) — see `lib/featureForm.ts`:
 *
 *   - `create` — POST   `/persons/{personGuid}/features`
 *   - `update` — PUT    `/persons/{personGuid}/features/{id}`
 *   - `remove` — DELETE `/persons/{personGuid}/features/{id}`
 *
 * Note on PUT semantics: the server replaces the row with the body we
 * send. The payload now includes every wire-required field (parentId,
 * childrenIds, supportingDocuments, source, dateObserved); if the row
 * needs additional fields to round-trip on edit, extend
 * `liveFieldsFromInput` and `FeatureInput`.
 */

export interface UpdateFeatureVariables {
  readonly id: string;
  readonly input: FeatureInput;
}

interface MutationContext {
  readonly previous: FeatureViewModel[] | undefined;
}

type CreateResult = UseMutationResult<
  FeatureViewModel,
  Error,
  FeatureInput,
  MutationContext
>;
type UpdateResult = UseMutationResult<
  FeatureViewModel,
  Error,
  UpdateFeatureVariables,
  MutationContext
>;
type RemoveResult = UseMutationResult<undefined, Error, string, MutationContext>;

export function useFeatureMutations(guid: string): {
  readonly create: CreateResult;
  readonly update: UpdateResult;
  readonly remove: RemoveResult;
} {
  const queryClient = useQueryClient();
  const { queryKey } = patientQueries.features(guid);

  const snapshot = (): MutationContext => ({
    previous: queryClient.getQueryData(queryKey),
  });

  const rollback = (ctx: MutationContext | undefined): void => {
    if (ctx === undefined) return;
    queryClient.setQueryData(queryKey, ctx.previous);
  };

  const settle = (): void => {
    queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
  };

  const create = useMutation<FeatureViewModel, Error, FeatureInput, MutationContext>({
    meta: { successMessage: 'success.toast.title.feature_created' },
    mutationFn: async (input) => {
      const dto = await apiClient.addFeature(guid, liveFieldsFromInput(input));
      return mapFeature(dto);
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      const optimistic = toOptimisticFeature(
        input,
        `optimistic-${Date.now().toString(36)}`,
      );
      queryClient.setQueryData<FeatureViewModel[]>(queryKey, [
        ...(ctx.previous ?? []),
        optimistic,
      ]);
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  const update = useMutation<
    FeatureViewModel,
    Error,
    UpdateFeatureVariables,
    MutationContext
  >({
    meta: { successMessage: 'success.toast.title.feature_updated' },
    mutationFn: async ({ id, input }) => {
      const dto = await apiClient.updateFeature(guid, id, liveFieldsFromInput(input));
      return mapFeature(dto);
    },
    onMutate: async ({ id, input }) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      queryClient.setQueryData<FeatureViewModel[]>(
        queryKey,
        (ctx.previous ?? []).map((f) =>
          f.id === id
            ? { ...toOptimisticFeature(input, id), createdAt: f.createdAt }
            : f,
        ),
      );
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  const remove = useMutation<undefined, Error, string, MutationContext>({
    meta: { successMessage: 'success.toast.title.feature_deleted' },
    mutationFn: (id) => apiClient.deleteFeature(guid, id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      queryClient.setQueryData<FeatureViewModel[]>(
        queryKey,
        (ctx.previous ?? []).filter((f) => f.id !== id),
      );
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  return { create, update, remove };
}
